import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@mui/styles';
import { AppBar, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';
import clsx from 'clsx';
import Breadcrumbs from '../ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from '../menu-items';
import { drawerWidth } from '../store/constant';
import { SET_MENU } from '../store/actions';
import { IconChevronRight } from '@tabler/icons-react';

/**
 * MainLayout component is the main layout of the application.
 * It contains the AppBar, Sidebar, and the main content.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components.
 * @return {ReactElement} The rendered MainLayout component.
 */
const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex'
    },
    appBar: {
        backgroundColor: '#1e73d8',
        color: '#fff',
        boxShadow: 'none',
        zIndex: theme.zIndex.drawer + 1
    },
    appBarWidth: {
        backgroundColor: '#1e73d8',
        transition: theme.transitions.create('width'),
    },
    content: {
        ...theme.typography.mainContent,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: 5,
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '5px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '5px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '5px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '5px',
            marginRight: '5px'
        }
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: drawerWidth ,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        [theme.breakpoints.down('md')]: {
            marginLeft: '5px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '5px'
        }
    },
    footer: {
        marginBottom: '80px'
    }
}));


const MainLayout = ({ children }) => {
    /**
     * Hooks
     */
    const classes = useStyles();
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const dispatch = useDispatch();

    /**
     * Functions
     */
    /**
     * Toggles the left drawer.
     */
    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };

    /**
     * Sets the left drawer state on mount.
     */
    React.useEffect(() => {
        if(!matchDownMd){
            dispatch({ type: SET_MENU, opened: !matchDownMd });
        } else {
            setTimeout(() => {
                dispatch({ type: SET_MENU, opened: !matchDownMd });
            }, 300)
        }
    }, [matchDownMd]);

    return (
        <div className={classes.root}>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                className={leftDrawerOpened ? `${classes.appBarWidth} no-print` : `${classes.appBar} no-print`}
            >   
                <Toolbar sx={{ minHeight: '64px !important', px: 2 }}>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>

            <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

            <main
                className={clsx([
                    classes.content,
                    {
                        [classes.contentShift]: leftDrawerOpened,
                        [classes.footer]: matchDownMd
                    }
                ])}
            >
                <Breadcrumbs
                    separator={IconChevronRight}
                    navigation={navigation}
                    sx={{
                        fontSize: 14,
                        color: '#1e73d8',
                        fontWeight: 500,
                        mb: 2
                    }}
                    />

                <div>{children}</div>
            </main>
        </div>
    );
};

MainLayout.propTypes = {
    children: PropTypes.node
};

export default MainLayout;
